import { SettingsSection } from '@components'

import { Alert, Button, Divider, Module, Space, Text } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ZervantSubscriptionPlanName } from '@features/zervant-subscription/enums/zervantSubscriptionPlanName'
import type { ZervantSubscription } from '@features/zervant-subscription/query-api'

import { formatDate } from '../../../../../../../utils'
import { ProgressChartComponent } from '../../ProgressChartComponent'
import { SummaryBoxesComponent } from '../../SummaryBoxesComponent'

const CENTS_PER_UNIT = 100
const UNLIMITED_QUOTA = -1

const PLAN_DISPLAY_NAME_KEYS: Partial<Record<ZervantSubscriptionPlanName, string>> = {
  [ZervantSubscriptionPlanName.AgerasBasic]: 'organization_subscription.ageras_basic',
  [ZervantSubscriptionPlanName.AgerasFree]: 'organization_subscription.ageras_free',
  [ZervantSubscriptionPlanName.AgerasPlus]: 'organization_subscription.ageras_plus',
  [ZervantSubscriptionPlanName.AgerasStart]: 'organization_subscription.ageras_start',
  [ZervantSubscriptionPlanName.Free]: 'organization_subscription.free',
  [ZervantSubscriptionPlanName.None]: 'organization_subscription.none',
  [ZervantSubscriptionPlanName.Pro]: 'organization_subscription.pro',
}

const KNOWN_PLAN_NAMES = ['start', 'plus', 'pro', 'premium', 'basic', 'free', 'growth', 'starter', 'merchant'] as const

const extractDowngradePlanName = (planId: string): string => {
  const segment = planId.split('_').find((part) => (KNOWN_PLAN_NAMES as readonly string[]).includes(part))
  if (!segment) {
    return planId
  }
  return segment.charAt(0).toUpperCase() + segment.slice(1)
}

interface StripePortalPlanSectionProps {
  zervantSubscription: ZervantSubscription | undefined
  isOpeningPortal: boolean
  onChangePlan: () => void
}

export const StripePortalPlanSection = ({
  zervantSubscription,
  isOpeningPortal,
  onChangePlan,
}: StripePortalPlanSectionProps): ReactElement => {
  const { t, i18n } = useTranslation()

  const billingCycle = zervantSubscription?.plan?.billingCycle ?? null

  const planDisplayName = useMemo(() => {
    const planKey = zervantSubscription?.plan?.name
    if (!planKey) {
      return ''
    }
    const tKey = PLAN_DISPLAY_NAME_KEYS[planKey]
    if (tKey) {
      return t(tKey)
    }
    return planKey.split(' ')[0]
  }, [zervantSubscription?.plan?.name, t])

  const billingDuration = useMemo(() => {
    if (billingCycle === 'year') {
      return t('organization_subscription.stripe_portal.billing_cycle_year')
    }
    if (billingCycle === 'month') {
      return t('organization_subscription.stripe_portal.billing_cycle_month')
    }
    return null
  }, [billingCycle, t])

  const isPastDue = zervantSubscription?.status === 'past_due'

  const cancelDate = useMemo(() => {
    if (!zervantSubscription?.cancelAt || zervantSubscription.inDowngrade) {
      return null
    }
    return formatDate(new Date(zervantSubscription.cancelAt), undefined, i18n.language) ?? null
  }, [zervantSubscription?.cancelAt, zervantSubscription?.inDowngrade, i18n.language])

  const renewalDate = useMemo(() => {
    if (isPastDue || cancelDate || !zervantSubscription?.currentPeriodEnd) {
      return null
    }
    return formatDate(new Date(zervantSubscription.currentPeriodEnd), undefined, i18n.language) ?? null
  }, [isPastDue, cancelDate, zervantSubscription?.currentPeriodEnd, i18n.language])

  const subscriptionSummaryItems = useMemo(() => {
    const planValue = billingDuration ? `${planDisplayName}, ${billingDuration}` : planDisplayName
    const items = [
      {
        name: t('organization_subscription.summary_current_plan'),
        value: planValue,
      },
    ]

    if (zervantSubscription?.plan?.price != null) {
      const priceInUnits = (zervantSubscription.plan.price / CENTS_PER_UNIT).toFixed(2)
      const currency = zervantSubscription.plan.currency?.toUpperCase() ?? ''
      const frequencySuffix =
        billingCycle === 'year'
          ? t('organization_subscription.stripe_portal.cost_per_year')
          : t('organization_subscription.stripe_portal.cost_per_month')
      items.push({
        name: t('organization_subscription.cost'),
        value: `${priceInUnits} ${currency}${frequencySuffix}`,
      })
    }

    return items
  }, [planDisplayName, billingDuration, billingCycle, zervantSubscription?.plan, t])

  const finiteQuotas = useMemo(
    () => (zervantSubscription?.quotas ?? []).filter((quota) => quota.total !== UNLIMITED_QUOTA),
    [zervantSubscription?.quotas],
  )

  return (
    <SettingsSection>
      <SettingsSection.RowsContainer>
        <SettingsSection.Content
          title={t('organization_subscription.subscription_overview')}
          sideActions={
            <Button loading={isOpeningPortal} onClick={onChangePlan}>
              {t('organization_subscription.stripe_portal.choose_plan_button')}
            </Button>
          }
        >
          <Module.Section title={t('organization_subscription.plan_details')}>
            <SummaryBoxesComponent items={subscriptionSummaryItems} />
            {isPastDue && (
              <>
                <Space size="s16" />
                <Alert variant="warning">{t('organization_subscription.stripe_portal.past_due_notice')}</Alert>
              </>
            )}
            {renewalDate && (
              <>
                <Space size="s16" />
                <Text variant="bodySM" colorVariant="secondary">
                  {zervantSubscription?.inDowngrade && zervantSubscription.downgradeTo ? (
                    t('organization_subscription.stripe_portal.downgrade_notice', {
                      planName: extractDowngradePlanName(zervantSubscription.downgradeTo),
                      date: renewalDate,
                    })
                  ) : (
                    <>
                      {t('organization_subscription.renewal_on')}
                      <strong>{renewalDate}</strong>.
                    </>
                  )}
                </Text>
              </>
            )}
            {cancelDate && (
              <>
                <Space size="s16" />
                <Alert variant="warning">
                  {t('organization_subscription.stripe_portal.cancel_notice', { date: cancelDate })}
                </Alert>
              </>
            )}
          </Module.Section>

          {finiteQuotas.length > 0 && (
            <>
              <Space size="s16" />
              <Divider />
              <Space size="s16" />
              <Module.Section title={t('organization_subscription.plan_usage')}>
                {finiteQuotas.map((quota) => (
                  <ProgressChartComponent
                    key={quota.type}
                    leftLabel={quota.type.replace(/_/g, ' ')}
                    rightLabel={`${quota.used} / ${quota.total}`}
                    value={quota.used}
                    total={quota.total}
                  />
                ))}
              </Module.Section>
            </>
          )}
        </SettingsSection.Content>
      </SettingsSection.RowsContainer>
    </SettingsSection>
  )
}
